import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseLocalApiOverride } from '../../../utils/parseLocalApiOverride';
import { isOAuth, isOAuthWithPolling } from '../../../utils/oauthHelpers';
import { useLowLevelAuthFlowMachine } from '../LowLevelAuthFlowMachineContext';
import { useHttpClient } from '../../../HttpClientContext';
import { useOAuthWindow } from './useOAuthWindow';
import { useSessionPolling } from './useSessionPolling';
import { captureException, captureMessage, toError } from '../../../../internal/ravenUtils';

// Once polling begins the popup is no longer an observable signal, so we show
// the spinner for this long and then hand off to silent background polling.
// This is measured against the wall clock rather than a poll count, which
// drifts from real time with network latency and timer throttling.
const SPINNER_VISIBLE_MS = 5000;
const isConvertibleAuthError = error => typeof error === 'object' && error !== null && 'toAuthError' in error && typeof error.toAuthError === 'function';

/**
 * Turns an error from starting an attempt into the flow's error: a typed auth
 * error as itself, otherwise the backend's message or the error's own, and
 * reports errors the UI can't explain.
 */
function toStartError(err, type, report) {
  var _error$responseJSON, _error$responseJSON2;
  if (isConvertibleAuthError(err)) {
    return err.toAuthError();
  }
  const error = err;
  if (!((_error$responseJSON = error.responseJSON) !== null && _error$responseJSON !== void 0 && _error$responseJSON.message)) {
    captureException(new Error(report.description, {
      cause: toError(err)
    }), {
      extra: {
        appId: report.appId,
        authFlowType: report.authFlowType
      }
    });
  }
  return {
    type,
    message: ((_error$responseJSON2 = error.responseJSON) === null || _error$responseJSON2 === void 0 ? void 0 : _error$responseJSON2.message) || toError(err).message
  };
}
export function useFinishAuth() {
  const httpClient = useHttpClient();
  const {
    state,
    send
  } = useLowLevelAuthFlowMachine();
  const {
    portalId,
    authConfig,
    params,
    authProcessStatus,
    oAuthState,
    oAuthScopeOverride,
    customParamsOverride,
    onComplete
  } = state.context;
  const {
    appName,
    appId,
    authFlow,
    mcpParams,
    oAuthScopeSet,
    customParams,
    buildAuthPayload,
    makeAuthorizeUrlDescriptor,
    finishAuthFlow
  } = authConfig;
  const {
    sessionId,
    steps
  } = authFlow;
  const authFlowType = authFlow.type;
  // The request the current attempt sends, after `beforeConnect`. Kept for
  // the OAuth callback, which finishes the attempt later.
  const attemptRef = useRef(null);
  // Bumped per attempt and on unmount, so a `beforeConnect` that resolves
  // late doesn't continue an attempt that's no longer current.
  const attemptIdRef = useRef(0);
  useEffect(() => () => {
    attemptIdRef.current += 1;
  }, []);
  const handleFailure = useCallback(error => {
    send({
      type: 'FINISH_FAILURE',
      error
    });
  }, [send]);
  const handleSuccess = useCallback(response => {
    send({
      type: 'FINISH_SUCCESS',
      response
    });
  }, [send]);
  const executeAuthFlow = useCallback(async authContext => {
    let response;
    try {
      const payload = await buildAuthPayload(authContext);
      response = await finishAuthFlow(payload);
    } catch (err) {
      var _error$responseJSON3;
      const error = err;
      if (isConvertibleAuthError(err)) {
        handleFailure(err.toAuthError());
      } else if ((_error$responseJSON3 = error.responseJSON) !== null && _error$responseJSON3 !== void 0 && _error$responseJSON3.message) {
        handleFailure({
          type: 'AuthenticateError',
          message: error.responseJSON.message
        });
      } else {
        // Neither a typed auth error nor a backend error response, so the
        // flow broke in a way the UI cannot explain to the user.
        captureException(new Error('Unexpected error while finishing auth flow', {
          cause: toError(err)
        }), {
          extra: {
            appId,
            authFlowType
          }
        });
        handleFailure({
          type: 'AuthenticateError',
          message: error.message
        });
      }
      return;
    }
    try {
      // Only tell the FSM the flow succeeded once the consumer's own
      // completion hook (e.g. onConnectComplete/onReconnectComplete) has
      // actually resolved. Committing to `success` beforehand left the FSM
      // stuck there with no way back if that hook rejected — retries would
      // silently open a new popup with no listener wired up to receive it.
      await onComplete(response);
    } catch (err) {
      // The completion hook already owns reporting/surfacing its own
      // failure to the user (that's its documented contract), so don't
      // reclassify or re-report it here — just send the flow back to a
      // retryable state.
      handleFailure({
        type: 'AuthenticateError',
        message: toError(err).message
      });
      return;
    }
    handleSuccess(response);
  }, [appId, authFlowType, buildAuthPayload, finishAuthFlow, onComplete, handleSuccess, handleFailure]);
  const isFinishingRef = useRef(false);
  const handleOAuthResponse = useCallback((oAuthParams, receivedState) => {
    if (receivedState !== oAuthState) {
      captureMessage('OAuth response state did not match the expected state', {
        extra: {
          appId
        }
      });
      handleFailure({
        type: 'UnknownOAuthProblem'
      });
      return;
    }

    // Guards against both the window-message and polling paths firing
    // simultaneously. Reset happens in startAuth on each new attempt.
    if (isFinishingRef.current) {
      return;
    }
    isFinishingRef.current = true;
    send({
      type: 'OAUTH_RESPONSE_RECEIVED',
      params: oAuthParams,
      state: receivedState
    });
    void executeAuthFlow({
      sessionId,
      steps,
      urlParams: params,
      oAuthParams,
      oAuthState: receivedState,
      oAuthScopeSet: attemptRef.current ? attemptRef.current.oAuthScopes : oAuthScopeOverride !== null && oAuthScopeOverride !== void 0 ? oAuthScopeOverride : oAuthScopeSet,
      customParams: attemptRef.current ? attemptRef.current.customParams : customParamsOverride !== null && customParamsOverride !== void 0 ? customParamsOverride : customParams,
      mcpParams,
      appId
    });
  }, [oAuthState, sessionId, steps, params, oAuthScopeOverride, oAuthScopeSet, customParamsOverride, customParams, mcpParams, appId, executeAuthFlow, handleFailure, send]);
  const oAuthCallbacks = useMemo(() => ({
    onWindowOpened: (oAuthWindow, oAuthStateValue) => {
      var _attemptRef$current$a, _attemptRef$current;
      send({
        type: 'OAUTH_WINDOW_OPENED',
        window: oAuthWindow,
        oAuthState: oAuthStateValue
      });
      if (isOAuthWithPolling((_attemptRef$current$a = (_attemptRef$current = attemptRef.current) === null || _attemptRef$current === void 0 ? void 0 : _attemptRef$current.authFlowType) !== null && _attemptRef$current$a !== void 0 ? _attemptRef$current$a : authFlowType)) {
        send({
          type: 'START_POLLING'
        });
      }
    },
    onWindowBlocked: () => {
      send({
        type: 'OAUTH_WINDOW_BLOCKED'
      });
    },
    onWindowClosed: () => {
      send({
        type: 'OAUTH_WINDOW_CLOSED'
      });
    },
    onWindowTimeout: () => {
      send({
        type: 'OAUTH_WINDOW_TIMEOUT'
      });
    },
    onAuthResponse: (oAuthParams, status) => {
      if (status === 'AUTH_RESPONSE') {
        const receivedState = oAuthParams.get('state') || '';
        handleOAuthResponse(oAuthParams, receivedState);
      }
    },
    onAuthError: message => {
      handleFailure({
        type: 'AuthorizeError',
        message
      });
    }
  }), [authFlowType, handleFailure, handleOAuthResponse, send]);
  const {
    openWindow
  } = useOAuthWindow({
    appName,
    callbacks: oAuthCallbacks,
    enabled: authProcessStatus === 'authorizing' || authProcessStatus === 'polling'
  });
  const pollingCallbacks = useMemo(() => ({
    onPollTick: () => {
      send({
        type: 'POLL_TICK'
      });
    },
    onPollSuccess: (pollParams, status) => {
      const stateParam = pollParams.get('state') || sessionId;
      send({
        type: 'POLL_SUCCESS',
        params: pollParams,
        status
      });
      if (status !== 'PENDING') {
        handleOAuthResponse(pollParams, stateParam);
      }
    },
    onPollError: () => {
      send({
        type: 'POLL_ERROR'
      });
    },
    onTimeout: () => {
      send({
        type: 'OAUTH_WINDOW_TIMEOUT'
      });
    }
  }), [handleOAuthResponse, send, sessionId]);
  const {
    stopPolling
  } = useSessionPolling({
    portalId,
    sessionId,
    callbacks: pollingCallbacks,
    enabled: authProcessStatus === 'polling',
    httpClient
  });
  const startAuth = useCallback((oAuthWindow, beforeConnect) => {
    const initialRequest = {
      customParams: customParamsOverride !== null && customParamsOverride !== void 0 ? customParamsOverride : customParams,
      oAuthScopes: oAuthScopeOverride !== null && oAuthScopeOverride !== void 0 ? oAuthScopeOverride : oAuthScopeSet,
      authFlowType
    };
    isFinishingRef.current = false;
    stopPolling();
    send({
      type: 'START_AUTH'
    });
    attemptIdRef.current += 1;
    const attemptId = attemptIdRef.current;
    attemptRef.current = null;
    const startOAuth = async request => {
      try {
        const authPayload = await buildAuthPayload({
          sessionId,
          steps,
          urlParams: params,
          oAuthScopeSet: request.oAuthScopes,
          customParams: request.customParams,
          oAuthState: sessionId,
          oAuthParams: new Map(),
          mcpParams,
          appId
        });
        const {
          authorizeUrl
        } = await makeAuthorizeUrlDescriptor(authPayload);
        const parsedUrl = parseLocalApiOverride(authorizeUrl);
        openWindow(sessionId, parsedUrl, oAuthWindow);
      } catch (err) {
        handleFailure(toStartError(err, 'AuthorizeError', {
          description: 'Unexpected error while starting OAuth flow',
          appId,
          authFlowType: request.authFlowType
        }));
      }
    };
    const authenticate = request => {
      send({
        type: 'START_AUTHENTICATING'
      });
      void executeAuthFlow({
        sessionId,
        steps,
        urlParams: params,
        customParams: request.customParams,
        appId
      });
    };
    const run = request => {
      attemptRef.current = request;
      if (isOAuth(request.authFlowType)) {
        void startOAuth(request);
      } else {
        authenticate(request);
      }
    };
    if (beforeConnect === undefined) {
      run(initialRequest);
      return;
    }
    const isCurrent = () => attemptId === attemptIdRef.current;
    // Starting from a resolved promise turns a synchronous throw into a
    // rejection, and the two-argument `then` keeps errors from `run` out
    // of the callback's failure handling.
    void Promise.resolve().then(() => beforeConnect(initialRequest)).then(request => {
      if (isCurrent()) {
        run(request);
      }
    }, err => {
      if (isCurrent()) {
        handleFailure(toStartError(err, isOAuth(authFlowType) ? 'AuthorizeError' : 'AuthenticateError', {
          description: 'Unexpected error in beforeConnect',
          appId,
          authFlowType
        }));
      }
    });
  }, [send, authFlowType, sessionId, steps, params, oAuthScopeOverride, oAuthScopeSet, customParamsOverride, customParams, mcpParams, appId, buildAuthPayload, makeAuthorizeUrlDescriptor, openWindow, executeAuthFlow, handleFailure, stopPolling]);
  const isActivelyAuthing = authProcessStatus === 'authorizing' || authProcessStatus === 'authenticating' || authProcessStatus === 'polling';

  // In a polling flow the popup closing isn't an observable signal, so rather
  // than spinning until the session times out we show the spinner for a fixed
  // window of real time once polling starts, then keep polling silently in the
  // background in case the user is still completing auth. Non-polling flows
  // never enter the polling state, so their spinner stays up for the whole flow.
  const [pollingSpinnerExpired, setPollingSpinnerExpired] = useState(false);
  useEffect(() => {
    if (authProcessStatus !== 'polling') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPollingSpinnerExpired(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setPollingSpinnerExpired(true);
    }, SPINNER_VISIBLE_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authProcessStatus]);
  const isLoading = isActivelyAuthing && !pollingSpinnerExpired;
  const hasFailed = authProcessStatus === 'failed' || authProcessStatus === 'cancelled' ||
  // Once the spinner stops while polling continues we can't confirm success,
  // so keep the button out of its completed (checkmark) state. Also
  // suppresses the checkmark while POST /secret is in flight.
  isActivelyAuthing && !isLoading;
  return {
    startAuth,
    isLoading,
    hasFailed,
    isActivelyAuthing
  };
}